import { useMemo } from 'react';
// routes
import { paths } from 'src/routes/paths';
// locales
import { useLocales } from 'src/locales';
// components
import SvgColor from 'src/components/svg-color';
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { useUnreadMessages } from 'src/context/UnreadMessagesContext';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
  // OR
  // <Iconify width={24} height={24} icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  benef: icon('ic_benef'),
  transfer: icon('ic_transfer'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
  check: icon('ic_check'),
  in: icon('ic_in'),
  out: icon('ic_out'),
  partners: icon('ic_partners'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useLocales();
  const { count } = useUnreadMessages();

  const data = useMemo(
    () => [
      {
        subheader: t('common.navbar.dashboard'),
        items: [
          { title: t('common.navbar.dashboard'), path: paths.lbanka.root, icon: <Iconify width={24} height={24} icon="duo-icons:dashboard" /> },
          { title: t('common.navbar.accounts'), path: paths.lbanka.accounts, icon: <Iconify width={24} height={24} icon="uim:bag" /> },
          {
            title: t('common.navbar.roles'), path: paths.lbanka.roles, icon: <Iconify width={24} height={24} icon="mingcute:user-lock-fill" />,
            caption: t('common.navbar.rolesText')
          },
        ],
      },

      {
        subheader: t('common.navbar.ch_eff'),
        items: [
          { title: t('common.navbar.checkbooks'), path: paths.lbanka.checkbooks, icon: <Iconify width={24} height={24} icon="material-symbols:checkbook-rounded" /> },
          {
            title: t('common.navbar.issued'), path: paths.lbanka.issued_checks, icon: <Iconify width={24} height={24} icon="solar:inbox-out-bold-duotone" />,
            caption: t('common.navbar.issuedtext')
          },
          {
            title: t('common.navbar.received'), path: paths.lbanka.recieved_checks, icon: <Iconify width={24} height={24} icon="solar:inbox-in-bold-duotone" />,
            caption: t('common.navbar.receivedText')
          },
          { title: t('common.navbar.calendar'), path: paths.lbanka.calendar, icon: <Iconify width={24} height={24} icon="solar:calendar-bold-duotone" /> },
          {
            title: t('common.navbar.partners'),
            path: paths.lbanka.partners,
            icon: <Iconify width={24} height={24} icon="fa-solid:users" />,
            info: (
              <Label
                color="info"
                variant="filled"
                sx={{
                  px: 0.5,
                  height: 20,
                }}
                startIcon={<Iconify width={24} height={24} icon="solar:bell-bing-bold-duotone" />}
              >
                {t('common.navbar.new')}
              </Label>
            ),
          },
        ],
      },

      {
        subheader: t('common.navbar.transfers'),
        items: [
          { title: t('common.navbar.transfers'), path: paths.lbanka.transfers, icon: <Iconify width={24} height={24} icon="fa6-solid:money-bill-transfer" /> },
          { title: t('common.navbar.local_beneficiaries'), path: paths.lbanka.local_beneficiaries, icon: <Iconify width={24} height={24} icon="fluent:person-money-24-filled" /> },
          { title: t('common.navbar.foreign_beneficiaries'), path: paths.lbanka.foreign_beneficiaries, icon: <Iconify width={24} height={24} icon="mdi:spoken-language" /> },
        ],
      },

      {
        subheader: "Guarantees or Bonds",
        items: [
          { title: t('cautions.projects.titles'), path: paths.lbanka.projects, icon: <Iconify width={24} height={24} icon="fluent:projection-screen-text-20-filled" /> },
          { title: t('cautions.guarantees.titles'), path: paths.lbanka.guarantees, icon: <Iconify width={24} height={24} icon="game-icons:cash" /> },
          { title: t('cautions.files.titles'), path: paths.lbanka.files, icon: <Iconify width={24} height={24} icon="solar:folder-with-files-bold-duotone" /> },
        ],
      },

      {
        subheader: t('common.navbar.help'),
        items: [
          {
            title: t('common.navbar.chat'),
            path: paths.lbanka.chat,
            icon: ICONS.chat,
            info: (
              <Label
                color="success"
                variant="outlined"
                sx={{
                  px: 0.5,
                  height: 20,
                }}
              >
                {count}
              </Label>
            ),
          },
        ],
      },
    ],
    [count, t]
  );

  return data;
}
